import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useStores } from 'Hooks/useStore';
import { useResetExpiredPassword } from 'Pages/Auth/hooks';
import { Button, Modal, Notification } from 'Components';
import { Input } from 'antd';
import { CognitoResponseError } from 'Services/Api/Auth/Types';
import { getAppRoutes } from 'Pages/App/App.constants';
import {
	COGNITO_ERROR_MSG,
	COGNITO_ERROR_MSG_TO_DESCRIPTION_MAP
} from 'app-config';
import styles from './ModalForm.module.scss';

interface Props {
	visible: boolean;
	onCancel: () => void;
}

const ForgotPasswordModal: React.FC<Props> = ({
	visible = false,
	onCancel
}) => {
	const [username, setUsername] = useState('');
	const [isLoading, setIsLoading] = useState(false);
	const { authStore } = useStores();
	const { t } = useTranslation();
	const history = useHistory();
	const appRoutes = getAppRoutes();
	const resetExpiredPassword = useResetExpiredPassword();

	const handleSubmit = async () => {
		if (!username.trim()) return;

		setIsLoading(true);
		try {
			await authStore.resetPassword({ login: username });

			Notification.success({
				description: t(
					'auth.resetPasswordSubmitForm.notifications.verificationCodeSent'
				)
			});

			onCancel(); // close modal
			history.push(
				`${appRoutes.RESET_PASSWORD}?login=${encodeURIComponent(
					username
				)}`
			);
		} catch (e) {
			const error = e as CognitoResponseError;

			if (error.code === 'UserNotFoundException') {
				Notification.error({
					description: t(
						'auth.resetPasswordSubmitForm.notifications.error.userNotFondException'
					)
				});
				return;
			}

			if (error.code === 'LimitExceededException') {
				Notification.error({
					description: t('auth.resetPassword.errors.limitExceeded')
				});
				return;
			}

			if (error.code === 'NotAuthorizedException') {
				if (error.message === COGNITO_ERROR_MSG.DISABLED) {
					Notification.error({
						description: t(
							COGNITO_ERROR_MSG_TO_DESCRIPTION_MAP[
								COGNITO_ERROR_MSG.DISABLED
							]
						)
					});
					return;
				}

				try {
					await resetExpiredPassword(username);
					onCancel();
					history.push(
						`${appRoutes.RESET_PASSWORD}?login=${encodeURIComponent(
							username
						)}`
					);
				} catch (innerError) {
					Notification.error({
						description: t('errorCodes.genericErrorMessage')
					});
				}
				return;
			}

			Notification.error({
				description: t('errorCodes.genericErrorMessage')
			});
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<Modal
			visible={visible}
			centered
			onCancel={onCancel}
			footer={null}
			size="xs"
		>
			<div className={styles.container}>
				<div className={styles.title}>{t('auth.resetPassword.title')}</div>
				<div className={styles.description}>
					{t('auth.resetPassword.description')}
				</div>
				<div className={styles.field}>
					<label>{t('auth.login.inputs.username')}</label>
					<Input
						placeholder={t('auth.login.inputs.usernameLabel')}
						value={username}
						size="large"
						onChange={(e) => setUsername(e.target.value)}
						onPressEnter={handleSubmit}
						disabled={isLoading}
					/>
				</div>

				<Button
					style={{ marginTop: '16px' }}
					type="primary"
					shape="round"
					className="cta-prominent"
					onClick={handleSubmit}
					loading={isLoading}
					disabled={!username.trim()}
				>
					{t('auth.resetPassword.submitText')}
				</Button>
			</div>
		</Modal>
	);
};

export default ForgotPasswordModal;
